// F2: AI 出力 JSON の取り込み（「ファイルパス + JSON コードブロック」形式のパースと配置）
import { existsSync, readFileSync } from 'node:fs';
import path from 'node:path';
import { containedPath, isProtectedPath, REPO_ROOT } from './repo-paths.mjs';
import { requireGameDir } from './games.mjs';
import { deepMergeInto, readJsonIfExists, writeJson } from './json-utils.mjs';
import { validateSingleJson } from './validate-schema.mjs';
import { auditLog } from './logger.mjs';

/**
 * AI 出力テキストをパースして [{relPath, jsonText, json, errors[]}] を返す。
 * 受け入れ形式: パス行（maps/ を含み .json で終わる）の後に ``` フェンスの JSON ブロック。
 * 対応するパス行が見つからずスキップしたフェンス数を entries.orphanBlocks に添える
 * （黙って捨てると取り込み漏れに気づけないため、呼び出し側が警告表示に使う）。
 */
export function parseAiOutput(text) {
    const entries = [];
    let orphanBlocks = 0;
    const fenceRe = /^[ \t]*(?:```|~~~)[^\n]*\n([\s\S]*?)^[ \t]*(?:```|~~~)[ \t]*$/gm;
    let match;
    const fences = [];
    while ((match = fenceRe.exec(text)) !== null) {
        fences.push({ start: match.index, end: fenceRe.lastIndex, body: match[1] });
    }
    let cursor = 0;
    for (const fence of fences) {
        // フェンス直前のテキストから最後のパス候補行を拾う
        // （見出し記号や装飾は除去せず、生の行に対して直接パスを抽出する。
        //   前処理でハイフンを潰すと admin-passwords.json 等の検出が壊れるため）
        const before = text.slice(cursor, fence.start);
        const lines = before.split(/\r?\n/).reverse();
        let relPath = null;
        for (const rawLine of lines) {
            const m = rawLine.match(/((?:[A-Za-z0-9_.<>\-]+\/)*maps\/[A-Za-z0-9_./\-]+\.json)/);
            if (m) {
                relPath = m[1];
                break;
            }
            if (rawLine.trim() !== '') break; // パスを含まない本文行に当たったら打ち切り
        }
        cursor = fence.end;
        if (!relPath) { orphanBlocks++; continue; }
        // <gameDir>/maps/... や newgames/xxx/maps/... を maps/... に正規化
        const idx = relPath.indexOf('maps/');
        const normalized = relPath.slice(idx);
        const entry = { relPath: normalized, jsonText: fence.body, json: null, errors: [], warnings: [] };
        if (/^\s*(\.\.\.|…)/m.test(fence.body)) {
            entry.errors.push('省略記号（... / …）が含まれています。完全な JSON を貼り付けてください。');
        }
        try {
            entry.json = JSON.parse(fence.body);
        } catch (e) {
            entry.errors.push(`JSON としてパースできません: ${e.message}（コメント・末尾カンマ・省略の可能性）`);
        }
        entries.push(entry);
    }
    entries.orphanBlocks = orphanBlocks;
    return entries;
}

/** ロケール JSON（ja/en）かどうか。
 *  AI 依頼文（F10）は翻訳を「locales への追加キー」として出力させるため、既存ロケールへの
 *  全置換を許すと messages / exact 等の既存キーが全消失する。既存ファイルがある場合は
 *  キー単位マージ（deepMergeInto）で取り込む。 */
function isLocaleRelPath(relPath) {
    return /^maps\/_shared\/i18n\/locales\/[a-z-]+\.json$/i.test(relPath);
}

/** 取り込みプランを作成（拒否判定・スキーマプリフライト・上書き差分）。 */
export function planImport(gameId, entries) {
    const gameDir = requireGameDir(gameId);
    const mapsDir = path.join(gameDir, 'maps');
    const pathCounts = new Map();
    for (const entry of entries) {
        pathCounts.set(entry.relPath, (pathCounts.get(entry.relPath) ?? 0) + 1);
    }
    const plan = [];
    for (const entry of entries) {
        const item = { ...entry, action: 'create', diff: null, schema: null };
        try {
            if (typeof entry.relPath !== 'string' || !entry.relPath.startsWith('maps/')) {
                throw new Error('配置先は対象ゲームの maps/ 配下のみ許可されます。');
            }
            const mapsRelative = entry.relPath.slice('maps/'.length);
            const segments = mapsRelative.split('/');
            if (!mapsRelative || segments.some((segment) => !segment || segment === '.' || segment === '..')) {
                throw new Error('配置先に空の要素や . / .. は使用できません。');
            }
            if (pathCounts.get(entry.relPath) > 1) {
                throw new Error(`同じ配置先が複数回指定されています: ${entry.relPath}`);
            }
            if (!entry.json || typeof entry.json !== 'object' || Array.isArray(entry.json)) {
                throw new Error('配置する JSON はオブジェクト形式である必要があります。');
            }
            // gameDir ではなく mapsDir を基準にする。`maps/../package.json` のような
            // ゲーム直下への脱出を文字列の startsWith だけで許可しない。
            const abs = containedPath(mapsDir, mapsRelative);
            if (isProtectedPath(abs, 'import')) {
                throw new Error('保護ファイル（運用系 / 自動生成）のため取り込みを拒否しました。');
            }
            item.absPath = abs;
            if (existsSync(abs)) {
                let existing = null;
                let corrupt = false;
                try {
                    existing = readJsonIfExists(abs);
                } catch {
                    // 既存 JSON の破損（マージコンフリクト等）は取り込み全体をブロックせず、
                    // 上書きによる修復を許可する（差分は算出不能のため注記のみ）
                    corrupt = true;
                }
                if (isLocaleRelPath(entry.relPath)) {
                    if (corrupt) throw new Error('既存のロケール JSON が破損しているためマージできません。先に手動で修復してください。');
                    item.action = 'merge';
                    const mergedPreview = structuredClone(existing);
                    deepMergeInto(mergedPreview, entry.json);
                    item.diff = summarizeDiff(existing, mergedPreview);
                } else {
                    item.action = 'overwrite';
                    if (corrupt) item.warnings.push('既存ファイルの JSON が破損しています（この上書きで修復されます）');
                    else item.diff = summarizeDiff(existing, entry.json);
                }
            }
            item.schema = validateSingleJson(gameId, entry.relPath, entry.json);
            if (item.schema && !item.schema.ok && !item.schema.skipped) {
                item.errors.push(`スキーマ検証 NG（${item.schema.schema}）: ${item.schema.errors.slice(0, 5).join(' / ')}`);
            }
        } catch (e) {
            item.errors.push(e.message);
        }
        plan.push(item);
    }
    return plan;
}

function summarizeDiff(oldJson, newJson) {
    if (!oldJson || !newJson) return null;
    const oldKeys = new Set(Object.keys(oldJson));
    const newKeys = new Set(Object.keys(newJson));
    const added = [...newKeys].filter((k) => !oldKeys.has(k));
    const removed = [...oldKeys].filter((k) => !newKeys.has(k));
    const changed = [...newKeys].filter((k) => oldKeys.has(k) && JSON.stringify(oldJson[k]) !== JSON.stringify(newJson[k]));
    return {
        addedTopKeys: added,
        removedTopKeys: removed,
        changedTopKeys: changed,
        oldSize: JSON.stringify(oldJson).length,
        newSize: JSON.stringify(newJson).length,
    };
}

/** プランをアトミックに適用。拒否/上書き未確認が 1 件でもあれば全件を未適用にする。
 *  merge（既存ロケールへのキー単位マージ）も既存ファイルを変更するため、overwrite と同じく確認必須。 */
export function applyImport(gameId, plan, { confirmOverwrite = false } = {}) {
    const modifiesExisting = (item) => item.action === 'overwrite' || item.action === 'merge';
    const hasErrors = plan.some((item) => item.errors.length > 0);
    const needsOverwriteConfirmation = !confirmOverwrite && plan.some(modifiesExisting);
    if (hasErrors || needsOverwriteConfirmation) {
        const results = plan.map((item) => {
            if (item.errors.length > 0) {
                return { relPath: item.relPath, status: 'rejected', reasons: item.errors };
            }
            if (modifiesExisting(item) && !confirmOverwrite) {
                return {
                    relPath: item.relPath,
                    status: 'skipped',
                    reasons: [item.action === 'merge'
                        ? '既存ファイルへのマージには confirmOverwrite（--overwrite）が必要です'
                        : '既存ファイルの上書きには confirmOverwrite（--overwrite）が必要です'],
                };
            }
            return { relPath: item.relPath, status: 'skipped', reasons: ['他のエントリに問題があるため、一括取り込みを適用しませんでした'] };
        });
        auditLog('import-json', {
            gameId,
            total: plan.length,
            written: 0,
            rejected: results.filter((r) => r.status === 'rejected').length,
            blocked: true,
        });
        return results;
    }

    const results = [];
    for (const item of plan) {
        if (item.action === 'merge') {
            // ロケールはキー単位マージ（既存キーを保全し、追加・変更キーだけ反映する）
            const merged = readJsonIfExists(item.absPath) ?? {};
            deepMergeInto(merged, item.json);
            writeJson(item.absPath, merged);
            results.push({ relPath: item.relPath, status: 'merged', reasons: [] });
        } else {
            writeJson(item.absPath, item.json);
            results.push({ relPath: item.relPath, status: item.action === 'overwrite' ? 'overwritten' : 'created', reasons: [] });
        }
    }
    auditLog('import-json', {
        gameId,
        total: plan.length,
        written: results.filter((r) => ['created', 'overwritten', 'merged'].includes(r.status)).length,
        rejected: results.filter((r) => r.status === 'rejected').length,
    });
    return results;
}

export function importFromText(gameId, text, { confirmOverwrite = false, dryRun = false } = {}) {
    const entries = parseAiOutput(text);
    const orphanBlocks = entries.orphanBlocks ?? 0;
    const plan = planImport(gameId, entries);
    if (dryRun) return { plan, orphanBlocks, results: null };
    return { plan, orphanBlocks, results: applyImport(gameId, plan, { confirmOverwrite }) };
}

export function importFromFile(gameId, filePath, opts = {}) {
    const text = readFileSync(path.resolve(filePath), 'utf-8');
    return importFromText(gameId, text, opts);
}
